import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@material-ui/core';
import { getLicenceTypeLabel } from './assignmentModel';
import { ISSUE_LABELS } from './assignmentFile';

const useStyles = makeStyles(theme => ({
    fileName: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        marginBottom: theme.spacing(2),
        overflowWrap: 'anywhere',
    },
    stats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    stat: {
        padding: theme.spacing(1, 1.25),
        border: '1px solid var(--dm-border, #e1e7ee)',
        borderRadius: 8,
    },
    statValue: {
        color: 'var(--dm-text-strong, #1f2f4a)',
        fontSize: '1.125rem',
        fontWeight: 700,
    },
    statAdd: { color: '#287a4b' },
    statRemove: { color: '#9b2c2c' },
    statFlag: { color: '#9a6700' },
    statLabel: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    sectionTitle: {
        margin: theme.spacing(2, 0, 1),
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.875rem',
        fontWeight: 700,
    },
    tableWrap: {
        maxHeight: 240,
        overflow: 'auto',
        border: '1px solid var(--dm-border, #e1e7ee)',
        borderRadius: 8,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.75rem',
        '& th': {
            position: 'sticky',
            top: 0,
            padding: theme.spacing(0.75, 1),
            textAlign: 'left',
            color: 'var(--dm-muted, #647184)',
            fontWeight: 700,
            backgroundColor: 'var(--dm-row-alt, #f8fafc)',
            borderBottom: '1px solid var(--dm-border, #e1e7ee)',
        },
        '& td': {
            padding: theme.spacing(0.75, 1),
            color: 'var(--dm-text-strong, #263548)',
            borderBottom: '1px solid var(--dm-border, #eef1f5)',
            verticalAlign: 'top',
            overflowWrap: 'anywhere',
        },
        '& tr:last-child td': {
            borderBottom: 0,
        },
    },
    numeric: {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
    },
    issueCode: {
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 4,
        backgroundColor: 'rgba(214, 158, 46, 0.14)',
        color: '#7b5a12',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    error: {
        padding: theme.spacing(1.5),
        borderRadius: 8,
        backgroundColor: 'rgba(197, 48, 48, 0.08)',
        color: '#9b2c2c',
        fontSize: '0.8125rem',
    },
    empty: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
    },
}));

const Stat = ({ value, label, className, classes }) => (
    <div className={classes.stat}>
        <div className={`${classes.statValue}${className ? ` ${className}` : ''}`}>{value ?? 0}</div>
        <div className={classes.statLabel}>{label}</div>
    </div>
);

const BulkAssignPreviewDialog = ({ open, fileName, preview, applying, onClose, onApply }) => {
    const classes = useStyles();
    const summary = preview?.summary || {};
    const changeCount = (summary.to_add || 0) + (summary.to_remove || 0);
    const issues = preview?.issues || [];
    const perLicence = (preview?.per_licence || []).filter(item => (
        item.to_add || item.to_remove || item.flagged
    ));

    return (
        <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="bulk-preview-title">
            <DialogTitle id="bulk-preview-title">Review bulk assignment</DialogTitle>
            <DialogContent>
                {fileName && <Typography className={classes.fileName}>File: {fileName}</Typography>}
                {preview?.error ? (
                    <div className={classes.error}>{preview.error}</div>
                ) : (
                    <>
                        <div className={classes.stats}>
                            <Stat classes={classes} value={summary.rows_total} label="Rows in file" />
                            <Stat classes={classes} value={summary.rows_matched} label="Users matched" />
                            <Stat classes={classes} value={summary.to_add} label="To assign" className={classes.statAdd} />
                            <Stat classes={classes} value={summary.to_remove} label="To remove" className={classes.statRemove} />
                            <Stat classes={classes} value={summary.rows_flagged} label="Rows flagged" className={classes.statFlag} />
                        </div>

                        <Typography className={classes.sectionTitle}>Changes by licence</Typography>
                        {perLicence.length ? (
                            <div className={classes.tableWrap}>
                                <table className={classes.table}>
                                    <thead>
                                        <tr>
                                            <th>Licence</th>
                                            <th>Type</th>
                                            <th className={classes.numeric}>Assign</th>
                                            <th className={classes.numeric}>Remove</th>
                                            <th className={classes.numeric}>Flagged</th>
                                            <th className={classes.numeric}>Left after</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {perLicence.map(item => (
                                            <tr key={item.licence_key}>
                                                <td>{item.name}</td>
                                                <td>{getLicenceTypeLabel(item.type || String(item.licence_key).split(':')[0])}</td>
                                                <td className={classes.numeric}>{item.to_add || 0}</td>
                                                <td className={classes.numeric}>{item.to_remove || 0}</td>
                                                <td className={classes.numeric}>{item.flagged || 0}</td>
                                                <td className={classes.numeric}>{item.remaining_after ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <Typography className={classes.empty}>The file does not change any assignments.</Typography>
                        )}

                        <Typography className={classes.sectionTitle}>
                            Rows needing review ({issues.length})
                        </Typography>
                        {issues.length ? (
                            <div className={classes.tableWrap}>
                                <table className={classes.table}>
                                    <thead>
                                        <tr>
                                            <th className={classes.numeric}>Row</th>
                                            <th>Work email</th>
                                            <th>Column</th>
                                            <th>Issue</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {issues.map((issue, index) => (
                                            <tr key={`${issue.row}-${issue.column}-${index}`}>
                                                <td className={classes.numeric}>{issue.row}</td>
                                                <td>{issue.email || '-'}</td>
                                                <td>{issue.column || '-'}</td>
                                                <td><span className={classes.issueCode}>{ISSUE_LABELS[issue.code] || issue.code}</span></td>
                                                <td>{issue.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <Typography className={classes.empty}>No issues found.</Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={applying}>Cancel</Button>
                {!preview?.error && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onApply}
                        disabled={applying || !changeCount}
                    >
                        {applying
                            ? <CircularProgress size={18} color="inherit" />
                            : `Apply ${changeCount} change${changeCount === 1 ? '' : 's'}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkAssignPreviewDialog;
